import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DayPicker } from "react-day-picker";
import { startOfMonth, endOfMonth, addDays, subDays } from "date-fns";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import "@/components/dashboard/availability-daypicker.css";

export function dateKey(d) {
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}

/**
 * Month picker that colours each day by the therapist's actual availability:
 *
 *   green   - available, and at least one slot of the chosen length is free
 *   amber   - working that day, but every slot is already taken
 *   plain   - not working
 *
 * The summary is computed server-side so the colour always matches what the
 * booking rules will allow.
 */
export function AvailabilityDayPicker({ adminId, value, onChange, durationMinutes = 45, excludeId }) {
  const [month, setMonth] = useState(value ? new Date(value) : new Date());

  const range = useMemo(
    () => ({ from: subDays(startOfMonth(month), 7), to: addDays(endOfMonth(month), 7) }),
    [month]
  );

  const { data, isFetching } = useQuery({
    queryKey: ["availability-days", adminId, dateKey(range.from), dateKey(range.to), durationMinutes, excludeId],
    queryFn: async () =>
      (
        await api.get(`/availability/${adminId}/days`, {
          params: {
            start: dateKey(range.from), end: dateKey(range.to),
            duration_minutes: durationMinutes, exclude_id: excludeId,
          },
        })
      ).data.days,
    enabled: !!adminId,
  });

  const { free, full } = useMemo(() => {
    const f = [];
    const b = [];
    Object.entries(data || {}).forEach(([k, v]) => {
      if (!v.has_hours) return;
      const [y, m, d] = k.split("-").map(Number);
      (v.free > 0 ? f : b).push(new Date(y, m - 1, d));
    });
    return { free: f, full: b };
  }, [data]);

  const selected = value ? new Date(`${value}T00:00:00`) : undefined;

  return (
    <div className="rounded-[10px] border border-border bg-white p-2" data-testid="availability-day-picker">
      <DayPicker
        mode="single"
        selected={selected}
        onSelect={(d) => d && onChange(dateKey(d))}
        month={month}
        onMonthChange={setMonth}
        weekStartsOn={1}
        showOutsideDays
        fromDate={subDays(new Date(), 1)}
        modifiers={{ free, full }}
        modifiersClassNames={{ free: "adp-free", full: "adp-full" }}
        className="adp"
      />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border px-2 pb-1 pt-2 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-[3px] bg-[hsl(142_55%_42%)]" /> Available
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-[3px] bg-[hsl(38_80%_50%)]" /> Fully booked
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-[3px] border border-border bg-white" /> Not working
        </span>
        {isFetching && <Loader2 className="ms-auto h-3.5 w-3.5 animate-spin text-primary" />}
      </div>
    </div>
  );
}
